import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function JobCalendar() {
  const navigate = useNavigate();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [dos, setDos] = useState([]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/delivery-orders/`).then((res) => setDos(res.data || [])).catch(() => {});
  }, []);

  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
  const byDay = useMemo(() => {
    const map = {};
    dos.forEach((d) => {
      const date = (d.delivery_date || '').slice(0, 10);
      if (date.startsWith(monthKey)) {
        (map[date] = map[date] || []).push(d);
      }
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => (a.delivery_time || '').localeCompare(b.delivery_time || '')));
    return map;
  }, [dos, monthKey]);

  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const todayISO = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const monthJobs = Object.values(byDay).reduce((s, a) => s + a.length, 0);

  const shiftMonth = (delta) => {
    let m = month + delta, y = year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setMonth(m); setYear(y);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-40 pb-16">
      <div className="container mx-auto px-4 max-w-6xl">
        <Button variant="outline" onClick={() => navigate('/admin/billing')} className="mb-5" data-testid="calendar-back-btn">
          <ArrowLeft size={18} className="mr-2" /> Back
        </Button>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#5c1a1a] flex items-center justify-center">
              <CalendarDays size={22} className="text-amber-300" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Job Calendar</h1>
              <p className="text-sm text-gray-500">{monthJobs} delivery order{monthJobs !== 1 ? 's' : ''} this month</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-white border rounded-lg px-2 py-1.5">
            <button onClick={() => shiftMonth(-1)} className="p-1.5 rounded hover:bg-gray-100" data-testid="cal-prev-month"><ChevronLeft size={18} /></button>
            <span className="font-semibold text-gray-900 w-36 text-center" data-testid="cal-month-label">
              {new Date(year, month, 1).toLocaleDateString('en-SG', { month: 'long', year: 'numeric' })}
            </span>
            <button onClick={() => shiftMonth(1)} className="p-1.5 rounded hover:bg-gray-100" data-testid="cal-next-month"><ChevronRight size={18} /></button>
          </div>
        </div>

        <div className="bg-white border rounded-xl overflow-hidden" data-testid="job-calendar-grid">
          <div className="grid grid-cols-7 bg-[#5c1a1a] text-white text-xs font-semibold">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
              <div key={d} className="px-2 py-2 text-center">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((day, i) => {
              if (!day) return <div key={`e${i}`} className="min-h-[90px] border-b border-r bg-gray-50/50" />;
              const iso = `${monthKey}-${String(day).padStart(2, '0')}`;
              const jobs = byDay[iso] || [];
              const isToday = iso === todayISO;
              return (
                <div key={iso} className={`min-h-[90px] border-b border-r p-1 ${isToday ? 'bg-amber-50' : jobs.length >= 3 ? 'bg-red-50/60' : ''}`} data-testid={`cal-day-${iso}`}>
                  <div className="flex items-center justify-between px-0.5">
                    <span className={`text-xs font-semibold ${isToday ? 'bg-[#5c1a1a] text-white rounded-full w-5 h-5 flex items-center justify-center' : 'text-gray-500'}`}>{day}</span>
                    {jobs.length > 0 && <span className="text-[10px] font-bold text-[#5c1a1a]">{jobs.length}</span>}
                  </div>
                  <div className="mt-0.5 space-y-0.5">
                    {jobs.slice(0, 3).map((j) => (
                      <button
                        key={j.id}
                        onClick={() => navigate(`/admin/billing/delivery-order/${j.id}`)}
                        className={`w-full text-left text-[10px] leading-tight px-1 py-0.5 rounded truncate block ${j.delivered ? 'bg-green-100 text-green-800' : 'bg-[#5c1a1a]/10 text-[#5c1a1a]'} hover:opacity-80`}
                        title={`${j.document_number} — ${j.customer_name} ${j.delivery_time || ''}`}
                        data-testid={`cal-job-${j.document_number}`}
                      >
                        {j.delivery_time ? `${j.delivery_time} ` : ''}{j.customer_name}
                      </button>
                    ))}
                    {jobs.length > 3 && <p className="text-[10px] text-gray-400 px-1">+{jobs.length - 3} more</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#5c1a1a]/10 border border-[#5c1a1a]/30 inline-block"></span> Pending</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-green-100 border border-green-300 inline-block"></span> Delivered</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-50 border border-red-200 inline-block"></span> Busy day (3+ jobs)</span>
        </div>
      </div>
    </div>
  );
}
